import { 
  collection, 
  addDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  doc, 
  query, 
  orderBy, 
  Timestamp, 
  where,
  DocumentData,
  QueryDocumentSnapshot,
  deleteDoc,
  setDoc,
  onSnapshot
} from 'firebase/firestore';
import { firestore } from '../firebase/config';

// Message status options
export enum MessageStatus {
  NEW = 'new',
  READ = 'read',
  RESPONDED = 'responded',
  ARCHIVED = 'archived'
}

// Message types
export enum MessageType {
  CONTACT = 'contact',
  BUG_REPORT = 'bug',
  FEEDBACK = 'feedback'
}

// Interface for contact messages
export interface ContactMessage {
  id?: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  projectType?: string;
  message: string;
  messageType: MessageType;
  toolId?: string;
  status: MessageStatus;
  createdAt: Date;
  readAt?: Date;
  respondedAt?: Date;
  notes?: string;
  ipAddress?: string;
  recaptchaToken?: string;
}

// Interface for blocked IP
export interface BlockedIP {
  ipAddress: string;
  blockedAt: Date;
  blockedBy: string;
  reason: string;
}

// Convert Firestore document to ContactMessage
const convertFromFirestore = (doc: QueryDocumentSnapshot<DocumentData>): ContactMessage => {
  const data = doc.data();
  
  return {
    id: doc.id,
    name: data.name || '',
    email: data.email || '',
    phone: data.phone || '',
    company: data.company || '',
    projectType: data.projectType || '',
    message: data.message || '',
    messageType: data.messageType || MessageType.CONTACT,
    toolId: data.toolId || undefined,
    status: data.status || MessageStatus.NEW,
    createdAt: data.createdAt?.toDate() || new Date(),
    readAt: data.readAt?.toDate() || undefined,
    respondedAt: data.respondedAt?.toDate() || undefined,
    notes: data.notes || '',
    ipAddress: data.ipAddress || undefined,
  };
};

// Add these constants
const MAX_MESSAGES_PER_HOUR = 5;
const HOUR_IN_MS = 60 * 60 * 1000;

// Contact messages service
export const messageService = {
  // Add a new contact message
  async addMessage(message: Omit<ContactMessage, 'id' | 'status' | 'createdAt' | 'ipAddress'>): Promise<string> {
    try {
      // Get user IP
      const userIP = await getUserIP();
      
      // Verify reCAPTCHA token if provided
      if (message.recaptchaToken) {
        const isValidToken = await verifyRecaptchaToken(message.recaptchaToken, userIP);
        if (!isValidToken) {
          throw new Error('reCAPTCHA verification failed. Please try again.');
        }
      } else {
        throw new Error('reCAPTCHA verification is required.');
      }
      
      // Add IP address to the message
      const messageToSave = {
        ...message,
        status: MessageStatus.NEW,
        createdAt: Timestamp.fromDate(new Date()),
        ipAddress: userIP
      };
      
      const docRef = await addDoc(collection(firestore, 'contactMessages'), messageToSave);
      return docRef.id;
    } catch (error) {
      console.error('Error adding contact message:', error);
      throw error;
    }
  },
  
  // Add tool feedback or bug report
  async addToolMessage(message: {
    name: string;
    email: string;
    message: string;
    messageType: MessageType.BUG_REPORT | MessageType.FEEDBACK;
    toolId: string;
    recaptchaToken?: string;
    phone?: string;
  }): Promise<string> {
    try {
      return await this.addMessage({
        ...message,
        phone: message.phone || 'N/A',
        messageType: message.messageType
      });
    } catch (error) {
      console.error('Error adding tool message:', error);
      throw error;
    }
  },
  
  // Get all contact messages with optional filtering
  async getMessages(status?: MessageStatus, messageType?: MessageType): Promise<ContactMessage[]> {
    try {
      let messagesQuery;
      
      if (status && messageType) {
        messagesQuery = query(
          collection(firestore, 'contactMessages'),
          where('status', '==', status),
          where('messageType', '==', messageType),
          orderBy('createdAt', 'desc')
        );
      } else if (status) {
        messagesQuery = query(
          collection(firestore, 'contactMessages'),
          where('status', '==', status),
          orderBy('createdAt', 'desc')
        );
      } else if (messageType) {
        messagesQuery = query(
          collection(firestore, 'contactMessages'),
          where('messageType', '==', messageType),
          orderBy('createdAt', 'desc')
        );
      } else {
        messagesQuery = query(
          collection(firestore, 'contactMessages'),
          orderBy('createdAt', 'desc')
        );
      }
      
      const snapshot = await getDocs(messagesQuery);
      return snapshot.docs.map(convertFromFirestore);
    } catch (error) {
      console.error('Error getting contact messages:', error);
      throw error;
    }
  },
  
  // Get a single contact message by ID
  async getMessageById(id: string): Promise<ContactMessage | null> {
    try {
      const messageDoc = await getDoc(doc(firestore, 'contactMessages', id));
      
      if (!messageDoc.exists()) {
        return null;
      }
      
      const data = messageDoc.data();
      
      return {
        id: messageDoc.id,
        ...data,
        messageType: data.messageType || MessageType.CONTACT,
        toolId: data.toolId || undefined,
        createdAt: (data.createdAt as Timestamp).toDate(),
        readAt: data.readAt ? (data.readAt as Timestamp).toDate() : undefined,
        respondedAt: data.respondedAt ? (data.respondedAt as Timestamp).toDate() : undefined
      } as ContactMessage;
    } catch (error) {
      console.error(`Error getting message with ID ${id}:`, error);
      throw error;
    }
  },
  
  // Update message status
  async updateMessageStatus(id: string, status: MessageStatus): Promise<void> {
    try {
      const messageRef = doc(firestore, 'contactMessages', id);
      
      const updateData: { status: MessageStatus; readAt?: Timestamp; respondedAt?: Timestamp } = { 
        status 
      };
      
      if (status === MessageStatus.READ && !(await this.getMessageById(id))?.readAt) {
        updateData.readAt = Timestamp.fromDate(new Date());
      }
      
      if (status === MessageStatus.RESPONDED) {
        updateData.respondedAt = Timestamp.fromDate(new Date());
      }
      
      await updateDoc(messageRef, updateData);
    } catch (error) {
      console.error(`Error updating message status for ID ${id}:`, error);
      throw error;
    }
  },
  
  // Add notes to a message
  async addNotes(id: string, notes: string): Promise<void> {
    try {
      const messageRef = doc(firestore, 'contactMessages', id);
      await updateDoc(messageRef, { notes });
    } catch (error) {
      console.error(`Error adding notes to message with ID ${id}:`, error);
      throw error;
    }
  },
  
  // Delete a message permanently
  async deleteMessage(id: string): Promise<void> {
    try {
      const messageRef = doc(firestore, 'contactMessages', id);
      await deleteDoc(messageRef);
    } catch (error) {
      console.error(`Error deleting message with ID ${id}:`, error);
      throw error;
    }
  },
  
  // Block an IP address
  async blockIP(ipAddress: string, blockedBy: string, reason: string): Promise<void> {
    try {
      const blockedIP: BlockedIP = {
        ipAddress,
        blockedAt: new Date(),
        blockedBy,
        reason
      };
      
      await setDoc(doc(firestore, 'blockedIPs', ipAddress), {
        ...blockedIP,
        blockedAt: Timestamp.fromDate(blockedIP.blockedAt)
      });
      
      console.log(`IP address ${ipAddress} has been blocked`);
    } catch (error) {
      console.error(`Error blocking IP address ${ipAddress}:`, error);
      throw error;
    }
  },
  
  // Unblock an IP address
  async unblockIP(ipAddress: string): Promise<void> {
    try {
      await deleteDoc(doc(firestore, 'blockedIPs', ipAddress));
      console.log(`IP address ${ipAddress} has been unblocked`);
    } catch (error) {
      console.error(`Error unblocking IP address ${ipAddress}:`, error);
      throw error;
    }
  },
  
  // Get all blocked IPs
  async getBlockedIPs(): Promise<BlockedIP[]> {
    try {
      const snapshot = await getDocs(collection(firestore, 'blockedIPs'));
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ipAddress: doc.id,
          blockedAt: data.blockedAt.toDate(),
          blockedBy: data.blockedBy,
          reason: data.reason
        };
      });
    } catch (error) {
      console.error('Error getting blocked IPs:', error);
      throw error;
    }
  },
  
  // Check if an IP is blocked
  async isIPBlocked(ipAddress: string): Promise<boolean> {
    try {
      const docRef = doc(firestore, 'blockedIPs', ipAddress);
      const docSnap = await getDoc(docRef);
      return docSnap.exists();
    } catch (error) {
      console.error(`Error checking if IP ${ipAddress} is blocked:`, error);
      return false;
    }
  },

  // Subscribe to new messages
  subscribeToNewMessages(callback: (message: ContactMessage) => void) {
    const messagesQuery = query(
      collection(firestore, 'contactMessages'),
      where('status', '==', MessageStatus.NEW),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(messagesQuery, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          callback(convertFromFirestore(change.doc));
        }
      });
    });
  }
};

// Function to detect spam content
const isSpam = (message: string): boolean => {
  if (!message) return false;
  
  // Common spam keywords
  const spamKeywords = [
    'viagra', 'cialis', 'casino', 'lottery', 'free money', 
    'bitcoin investment', 'crypto investment', 'make money fast',
    'online pharmacy', 'cheap meds', 'replica watches', 'weight loss',
    'enlarge', 'work from home', 'earn money online', 'no investment',
    'forex trading', 'binary options', 'get rich', 'million dollars'
  ];
  
  // Check for spam keywords
  if (spamKeywords.some(keyword => message.toLowerCase().includes(keyword))) {
    return true;
  }
  
  // Check for excessive URLs
  const urlCount = (message.match(/https?:\/\//g) || []).length;
  if (urlCount > 3) {
    return true;
  }
  
  // Check for excessive capitalization (shouting)
  const upperCaseChars = message.replace(/[^A-Z]/g, '').length;
  const totalChars = message.replace(/\s/g, '').length;
  if (totalChars > 20 && upperCaseChars / totalChars > 0.7) {
    return true;
  }
  
  return false;
};

// Function to get user IP (client-side implementation)
const getUserIP = async (): Promise<string> => {
  try {
    // Use a service like ipify to get the user's IP
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Error getting IP:', error);
    return 'unknown';
  }
};

// Function to validate email domain
const isValidEmailDomain = async (email: string): Promise<boolean> => {
  if (!email || !email.includes('@')) return false;
  
  const domain = email.split('@')[1];
  
  // List of common disposable email domains
  const disposableDomains = [
    'mailinator.com', 'tempmail.com', 'throwawaymail.com', 
    'guerrillamail.com', '10minutemail.com', 'yopmail.com',
    'tempinbox.com', 'dispostable.com', 'mailnesia.com',
    'trashmail.com', 'sharklasers.com', 'guerrillamail.info'
  ];
  
  // Check if domain is in the disposable list
  if (disposableDomains.includes(domain.toLowerCase())) {
    return false;
  }
  
  // Basic check for domain validity
  // In a real implementation, you might want to check MX records
  // but that requires server-side code
  return true;
};

// Function to verify reCAPTCHA token
const verifyRecaptchaToken = async (token: string, ipAddress: string): Promise<boolean> => {
  try {
    if (!process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
      console.error('Missing reCAPTCHA site key in environment variables');
      return false;
    }

    // For development environment, accept the token
    if (process.env.NODE_ENV === 'development') {
      console.warn('Accepting reCAPTCHA token in development environment');
      return true;
    }

    // Verify token using backend service
    const response = await fetch('https://www.google.com/recaptcha/api/siteverify', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        secret: process.env.RECAPTCHA_SECRET_KEY || '',
        response: token,
        remoteip: ipAddress
      }).toString()
    });

    if (!response.ok) {
      console.error('reCAPTCHA verification request failed');
      return false;
    }

    const result = await response.json();
    return result.success === true;
  } catch (error) {
    console.error('Error verifying reCAPTCHA token:', error);
    return false;
  }
}; 